<template>
  <v-abstract-menu-option :dialog-width="500" :showing-dialog.sync="showingDialog">
    <template #info>Categorías de sellers: {{ appSettings.sellersCategories.length }}</template>
    <template #input>
      <v-card>
        <v-container class="px-1 py-2">
          <v-combobox
            v-model="selectedCategories"
            append-outer-icon="send"
            chips
            clearable
            @click:append-outer="setCategoriesAndCloseDialog"
            deletable-chips
            :disabled="disabled"
            item-text="displayableText"
            item-value="url"
            :items="categoriesWithDisplayableText"
            :label="label"
            :loading="loadingCategories"
            multiple
            no-data-text="Al parecer no pudimos cargar las categorías"
            open-on-clear
            prepend-icon="category"
            small-chips
          >
            <!-- Customize the items in the selection list -->
            <template #item="{ item }">
              <span>{{ item.sellerName }} - {{ item.name }}</span>
            </template>
          </v-combobox>
        </v-container>
      </v-card>
    </template>
  </v-abstract-menu-option>
</template>

<script>
import api from "@/api";

import VAbstractMenuOption from "./AbstractMenuOption";

/**
 * A component that is listening to the app setting sellers ids and minLastPriceUpdate
 * in the store with the given `namespace` to get its categories and select some of them
 * and set them in the store in the `appSettings.sellersCategories` attribute.
 *
 * This component has the mutations for the store ready to use to add them
 * to your own store.
 *
 * It assumes that your store has the action `setAppSettingsSellersCategories`
 * to dispatch other actions (and update the data of the app) that will call
 * the mutation with the same name.
 */
export default {
  components: {
    VAbstractMenuOption
  },
  props: {
    /**
     * The namespace of the module of the store where to extract the `appSettings`
     * object and dispatch the actions.
     */
    namespace: {
      type: String,
      required: true
    }
  },
  data: () => ({
    categories: [],
    loadingCategories: false,
    selectedCategories: [],
    showingDialog: false
  }),
  computed: {
    /**
     * Get the `appSettings` object from the namespaced module of the store.
     */
    appSettings() {
      return this.$store.state[this.namespace].appSettings;
    },
    /**
     * Add a displayable text to the categories to use the power of the combobox's search.
     */
    categoriesWithDisplayableText() {
      return this.categories.map(category => ({
        ...category,
        displayableText: `${category.sellerName} ${category.name}`
      }));
    },
    /**
     * The combobox must be disabled if we don't have sellers and minDate.
     */
    disabled() {
      return !(
        this.sellersIds.length &&
        this.minDate &&
        this.minDate.match(/^\d{4}-\d{2}-\d{2}$/)
      );
    },
    /**
     * Set the correspondent label to the combobox depending if the item
     * is disabled or not and indicate why the item is disabled.
     */
    label() {
      if (!this.disabled) {
        return "Filtrar por categorías de los sellers";
      }

      if (!this.sellersIds.length) {
        return "Debes seleccionar al menos un seller";
      }

      return "Debes seleccionar una fecha mínima de LastPriceUpdate";
    },
    /**
     * Get the min date from the app settings minLastPriceUpdate.
     */
    minDate() {
      return this.appSettings.minLastPriceUpdate;
    },
    /**
     * Get the ids of the selected sellers from the store.
     */
    sellersIds() {
      return this.appSettings.sellersIds;
    }
  },
  methods: {
    /**
     * Get the categories to use in the combobox if there is no current category.
     */
    async getCategories() {
      if (!this.disabled) {
        this.loadingCategories = true;
        await api.global.categories
          .get({ sellersIds: this.sellersIds, minDate: this.minDate })
          .then(categories => {
            this.categories = categories;
          })
          .catch(() => {
            this.$notify.error(
              "No pudimos obtener las categories de los sellers"
            );
          });
        this.loadingCategories = false;
      }
    },
    /**
     * Set the selected categories in the store by calling the
     * `setAppSettingsSellersCategories` action and close the dialog that
     * shows the combobox.
     */
    setCategoriesAndCloseDialog() {
      this.$store.dispatch(
        `${this.namespace}/setAppSettingsSellersCategories`,
        this.selectedCategories
      );
      this.showingDialog = false;
    }
  },
  /**
   * Get the categories when the component is created to populate the
   * combobox and then set the selected categories that are in the store.
   */
  created() {
    this.getCategories();
    this.selectedCategories = this.appSettings.sellersCategories;
  },
  watch: {
    /**
     * Watch the minDate setting to get again the categories if it changes.
     */
    minDate() {
      if (this.minDate) {
        return this.getCategories();
      } else {
        this.categories = [];
        this.selectedCategories = [];
      }
    },
    /**
     * Watch the selected sellers ids to get their categories or clean
     * the selected categories.
     */
    sellersIds() {
      if (this.sellersIds.length) {
        this.getCategories();
        this.selectedCategories = this.selectedCategories.filter(category =>
          this.sellersIds.includes(category.sellerId)
        );
      } else {
        this.categories = [];
        this.selectedCategories = [];
      }

      this.setCategoriesAndCloseDialog();
    }
  }
};
</script>